/**
 * Net worth + balances-by-tax-type over time. One axis, calm chrome, series
 * colors from the validated palette (CSS vars), crosshair tooltip.
 */
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { YearSnapshot } from '../engine/snapshot';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  snapshots: YearSnapshot[];
  todaysDollars: boolean;
}

export function NetWorthChart({ snapshots, todaysDollars }: Props) {
  const data = snapshots.map((s) => {
    const f = todaysDollars ? s.cumulativeInflationFactor : 1;
    return {
      year: s.year,
      'Pre-tax': Math.round(s.balancesByFamily.pretax / f),
      Roth: Math.round(s.balancesByFamily.roth / f),
      'After-tax': Math.round(s.balancesByFamily.aftertax / f),
      HSA: Math.round(s.balancesByFamily.hsa / f),
      netWorth: Math.round(s.netWorth / f),
    };
  });

  const hasHsa = data.some((d) => d.HSA > 0);

  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid stroke="var(--grid)" strokeWidth={1} vertical={false} />
          <XAxis
            dataKey="year"
            stroke="var(--axis)"
            tickLine={false}
            tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
            tickMargin={8}
            minTickGap={28}
          />
          <YAxis
            stroke="var(--axis)"
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
            tickFormatter={(v: number) => fmtCompactUsd(v)}
            width={64}
          />
          <Tooltip
            cursor={{ stroke: 'var(--axis)', strokeWidth: 1 }}
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--ink)',
            }}
            formatter={(value) => fmtUsd(Number(value))}
            labelStyle={{ color: 'var(--ink-secondary)', fontWeight: 600 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 13, color: 'var(--ink-secondary)' }}
            iconType="plainline"
          />
          <Area
            type="monotone" dataKey="Pre-tax" stackId="bal"
            stroke="var(--series-1)" fill="var(--series-1)" fillOpacity={0.55} strokeWidth={2}
          />
          <Area
            type="monotone" dataKey="Roth" stackId="bal"
            stroke="var(--series-3)" fill="var(--series-3)" fillOpacity={0.55} strokeWidth={2}
          />
          <Area
            type="monotone" dataKey="After-tax" stackId="bal"
            stroke="var(--series-2)" fill="var(--series-2)" fillOpacity={0.55} strokeWidth={2}
          />
          {hasHsa && (
            <Area
              type="monotone" dataKey="HSA" stackId="bal"
              stroke="var(--series-4)" fill="var(--series-4)" fillOpacity={0.55} strokeWidth={2}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
